// ---------------------------------------------------------------------------
// Typed property values (spec 49) — one place that knows how each field type
// displays, filters, groups, and sorts. The rest of the collections UI stays
// type-agnostic by routing every value through these pure helpers.
//
//   select    attrs value = optionId (string)   finite domain
//   text      attrs value = string              open domain
//   number    attrs value = number              open domain
//   checkbox  attrs value = boolean             finite domain (true/false)
//
// select stores option IDS (labels live in the vocabulary), so an option
// rename is a one-row edit and every value/filter/view follows (spec 48 §4).
// The other types store their literal value. multi_select/date/rating are
// later phases (spec 49 §9).
// ---------------------------------------------------------------------------

import type { AttrValue, LibraryProperty, LibraryPropertyType } from '@/types/shared'

/** Catalogue for the "add property" type picker, in menu order. */
export const PROPERTY_TYPES: { type: LibraryPropertyType; label: string; hint: string }[] = [
  { type: 'select', label: 'Select', hint: 'One option from a list' },
  { type: 'text', label: 'Text', hint: 'Free-form text' },
  { type: 'number', label: 'Number', hint: 'A numeric value' },
  { type: 'checkbox', label: 'Checkbox', hint: 'A yes / no toggle' },
]

/** Field types whose values enumerate a finite set — eligible for group-by,
    matrix axes, and faceted value lists. text/number are open-ended. */
export function isFiniteDomain(type: LibraryPropertyType): boolean {
  return type === 'select' || type === 'checkbox'
}

/** select carries an option vocabulary; the others don't. */
export function usesOptions(type: LibraryPropertyType): boolean {
  return type === 'select'
}

/** Is a value present (non-empty)? number 0 and checkbox false count as set. */
export function hasAttrValue(v: AttrValue | undefined | null): boolean {
  if (v === undefined || v === null) return false
  if (typeof v === 'string') return v.trim().length > 0
  return true
}

/** A non-boolean value kept across a type switch to checkbox. Rendered raw —
    never masked as unchecked — and replaced only by a deliberate click. */
export function isCheckboxLeftover(v: AttrValue | undefined | null): boolean {
  return typeof v !== 'boolean' && hasAttrValue(v)
}

function optionLabelOf(prop: LibraryProperty, id: string): string {
  return prop.options.find(o => o.id === id)?.value ?? ''
}

function formatNumber(n: number, precision?: number): string {
  return typeof precision === 'number' ? n.toFixed(precision) : String(n)
}

/** A short human string for a value. '' when unset (checkbox never — it reads
    as Yes/No). Used for cell display, chips, and the lightbox caption. */
export function formatAttrValue(prop: LibraryProperty | undefined, v: AttrValue | undefined): string {
  if (!prop) return ''
  if (prop.type === 'checkbox') {
    // Leftovers render raw — masking them as 'No' would hide data the user
    // could then overwrite.
    if (isCheckboxLeftover(v)) return String(v)
    return v === true ? 'Yes' : 'No'
  }
  if (!hasAttrValue(v)) return ''
  switch (prop.type) {
    case 'select':
      // Unknown id → show the raw value: a literal kept across a type switch
      // to select (or a dangling option id) must stay visible, not go blank.
      return optionLabelOf(prop, String(v)) || String(v)
    case 'number':
      // Value may be a leftover from a same-key property of another type
      // (delete → recreate); show it raw rather than crash on .toFixed.
      return typeof v === 'number' ? formatNumber(v, prop.config?.precision) : String(v)
    case 'text':
      return String(v)
  }
}

// --- Faceted filter / group tokens -------------------------------------------
// Filters and group-by work on string "tokens": a value contributes a token,
// and an asset matches a token if its value yields it. This is what lets the
// one faceted-filter UI serve every type. Tokens are rename-proof (option ids
// for select; the literal for the rest).

/** The tokens an asset's value contributes for `prop`. An unset checkbox reads
    as 'false' (so "unchecked" is filterable); other unset values yield none. */
export function facetTokens(prop: LibraryProperty, v: AttrValue | undefined): string[] {
  if (prop.type === 'checkbox') {
    // Kept non-boolean leftovers facet as their own raw token, not as 'false'.
    if (isCheckboxLeftover(v)) return [String(v)]
    return [v === true ? 'true' : 'false']
  }
  if (!hasAttrValue(v)) return []
  switch (prop.type) {
    case 'select':
    case 'text':
      return [String(v)]
    case 'number':
      return [String(v as number)]
  }
}

/** Display label for a facet token (inverse of facetTokens). */
export function facetTokenLabel(prop: LibraryProperty, token: string): string {
  switch (prop.type) {
    case 'select':
      return optionLabelOf(prop, token) || token
    case 'checkbox':
      return token === 'true' ? 'Yes' : token === 'false' ? 'No' : token
    case 'number': {
      // Kept non-numeric leftovers label as themselves, not 'NaN'.
      const n = Number(token)
      return Number.isFinite(n) ? formatNumber(n, prop.config?.precision) : token
    }
    case 'text':
      return token
  }
}

/** Distinct tokens present across `assets`, in a sensible display order. */
export function valuesPresent(prop: LibraryProperty, values: (AttrValue | undefined)[]): string[] {
  const present = new Set<string>()
  for (const v of values) for (const tk of facetTokens(prop, v)) present.add(tk)
  if (usesOptions(prop.type)) {
    const ordered = prop.options.map(o => o.id).filter(id => present.has(id))
    present.forEach(tk => {
      if (!ordered.includes(tk)) ordered.push(tk)
    })
    return ordered
  }
  if (prop.type === 'checkbox') {
    // Yes/No first, then any raw leftover tokens so they stay filterable.
    const rest = [...present].filter(tk => tk !== 'true' && tk !== 'false').sort((a, b) => a.localeCompare(b))
    return [...['true', 'false'].filter(tk => present.has(tk)), ...rest]
  }
  if (prop.type === 'number') {
    // Numeric ascending; non-numeric leftovers last, alphabetical.
    const nums = [...present].filter(tk => Number.isFinite(Number(tk))).sort((a, b) => Number(a) - Number(b))
    const rest = [...present].filter(tk => !Number.isFinite(Number(tk))).sort((a, b) => a.localeCompare(b))
    return [...nums, ...rest]
  }
  return [...present].sort((a, b) => a.localeCompare(b))
}

/** OR within a facet: does the asset's value match any selected token? */
export function assetMatchesFacet(prop: LibraryProperty, v: AttrValue | undefined, selected: string[]): boolean {
  if (!selected.length) return true
  const tokens = facetTokens(prop, v)
  return selected.some(tk => tokens.includes(tk))
}

/** Does the asset's value belong to group `token`? */
export function assetInGroup(prop: LibraryProperty, v: AttrValue | undefined, token: string): boolean {
  return facetTokens(prop, v).includes(token)
}

// --- Sorting -----------------------------------------------------------------

/** Compare two values of the same property for sorting (ascending). */
export function compareAttr(prop: LibraryProperty, va: AttrValue | undefined, vb: AttrValue | undefined): number {
  const aSet = hasAttrValue(va)
  const bSet = hasAttrValue(vb)
  if (prop.type !== 'checkbox') {
    if (!aSet && !bSet) return 0
    if (!aSet) return 1 // empties sort last
    if (!bSet) return -1
  }
  switch (prop.type) {
    case 'number': {
      // Mirror valuesPresent: numeric-parseable values (including string
      // leftovers like '10') sort numerically; the rest sort after them,
      // alphabetical among themselves — NaN would poison the comparator and
      // scramble even the genuinely numeric rows.
      const na = typeof va === 'number' ? va : Number(String(va))
      const nb = typeof vb === 'number' ? vb : Number(String(vb))
      const aNum = Number.isFinite(na)
      const bNum = Number.isFinite(nb)
      if (aNum !== bNum) return aNum ? -1 : 1
      return aNum ? na - nb : String(va).localeCompare(String(vb))
    }
    case 'text':
      return String(va).localeCompare(String(vb))
    case 'checkbox': {
      // unchecked/unset → checked → leftovers last, alphabetical among themselves.
      const rank = (v: AttrValue | undefined) => (isCheckboxLeftover(v) ? 2 : v === true ? 1 : 0)
      const d = rank(va) - rank(vb)
      if (d !== 0) return d
      return rank(va) === 2 ? String(va).localeCompare(String(vb)) : 0
    }
    case 'select': {
      const idx = (v: AttrValue | undefined) => {
        const i = prop.options.findIndex(o => o.id === v)
        return i < 0 ? 999 : i
      }
      return idx(va) - idx(vb)
    }
  }
}

// --- Editing -----------------------------------------------------------------

/** Parse raw editor input into a stored value (or null to clear). */
export function coerceInput(type: LibraryPropertyType, raw: string): AttrValue | null {
  const s = raw.trim()
  if (!s) return null
  if (type === 'number') {
    const n = Number(s)
    return Number.isFinite(n) ? n : null
  }
  return s
}
